import { useState, useMemo } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Slider } from "./ui/slider";
import {
  Calculator, RotateCcw, Snowflake, Users, Layers, Sun,
  Maximize, ArrowRight, Info, CheckCircle2, Home
} from "lucide-react";

const ceilingFactors = [
  { value: "8", label: "Up to 10 ft", factor: 1.0 },
  { value: "11", label: "10–12 ft", factor: 1.08 },
  { value: "13", label: "12–14 ft", factor: 1.15 },
  { value: "15", label: "Above 14 ft", factor: 1.25 },
];

const windowSizes = [
  { value: "small", label: "Small", factor: 1.05 },
  { value: "medium", label: "Medium", factor: 1.10 },
  { value: "large", label: "Large", factor: 1.18 },
];

const windowDirections = [
  { value: "north", label: "North", bonus: 0 },
  { value: "east", label: "East", bonus: 0 },
  { value: "south", label: "South", bonus: 0.05 },
  { value: "west", label: "West", bonus: 0.05 },
];

const floorOptions = [
  { value: "ground", label: "Ground Floor", factor: 1.05 },
  { value: "middle", label: "Middle Floor", factor: 1.0 },
  { value: "top", label: "Top Floor", factor: 1.15 },
];

const roomTypes = [
  { value: "bedroom", label: "Bedroom", btu: 110 },
  { value: "living", label: "Living Room / Hall", btu: 120 },
  { value: "kitchen", label: "Kitchen", btu: 130 },
  { value: "study", label: "Study / Home Office", btu: 115 },
  { value: "master", label: "Master Bedroom", btu: 115 },
];

const tonnageRanges = [
  { min: 0, max: 0.65, tonnage: "0.75 Ton", system: "Split AC (Window/Portable suitable)" },
  { min: 0.65, max: 1.15, tonnage: "1.0 Ton", system: "Split AC" },
  { min: 1.15, max: 1.65, tonnage: "1.5 Ton", system: "Split AC" },
  { min: 1.65, max: 2.25, tonnage: "2.0 Ton", system: "Split AC / High-capacity Split" },
  { min: 2.25, max: 3.5, tonnage: "2.5–3.0 Ton", system: "Cassette AC / Ductable Split" },
  { min: 3.5, max: Infinity, tonnage: "3.0+ Ton", system: "Cassette / Ductable AC system recommended" },
];

export const ResidentialCalculator = ({ onGetQuote }) => {
  const [roomLength, setRoomLength] = useState("");
  const [roomWidth, setRoomWidth] = useState("");
  const [ceilingHeight, setCeilingHeight] = useState("");
  const [people, setPeople] = useState([2]);
  const [windowSize, setWindowSize] = useState("");
  const [windowDir, setWindowDir] = useState("");
  const [floor, setFloor] = useState("");
  const [roomType, setRoomType] = useState("");
  const [calculated, setCalculated] = useState(false);

  const result = useMemo(() => {
    const l = parseFloat(roomLength);
    const w = parseFloat(roomWidth);
    if (!l || !w || !ceilingHeight || !windowSize || !floor || !roomType) return null;

    const area = l * w;
    const room = roomTypes.find(r => r.value === roomType);
    const baseBTU = area * (room?.btu || 120);

    const ceiling = ceilingFactors.find(c => c.value === ceilingHeight);
    const afterCeiling = baseBTU * (ceiling?.factor || 1.0);

    const extraPeople = Math.max(0, people[0] - 2);
    const peopleBTU = extraPeople * 600;

    const win = windowSizes.find(ws => ws.value === windowSize);
    const winDir = windowDirections.find(wd => wd.value === windowDir);
    const windowFactor = (win?.factor || 1.0) + (winDir?.bonus || 0);

    const floorObj = floorOptions.find(f => f.value === floor);
    const floorFactor = floorObj?.factor || 1.0;

    const climateFactor = 1.08;

    const totalBTU = (afterCeiling + peopleBTU) * windowFactor * floorFactor * climateFactor;
    const rawTonnage = totalBTU / 12000;

    const rec = tonnageRanges.find(r => rawTonnage >= r.min && rawTonnage < r.max)
      || tonnageRanges[tonnageRanges.length - 1];

    return {
      area, totalBTU: Math.round(totalBTU), rawTonnage: rawTonnage.toFixed(2),
      recommendation: rec,
      inputs: {
        roomLength: l, roomWidth: w, area, ceilingHeight,
        people: people[0], windowSize, windowDirection: windowDir || "N/A",
        floor, roomType,
      },
    };
  }, [roomLength, roomWidth, ceilingHeight, people, windowSize, windowDir, floor, roomType]);

  const handleCalculate = () => { if (result) setCalculated(true); };
  const handleReset = () => {
    setRoomLength(""); setRoomWidth(""); setCeilingHeight("");
    setPeople([2]); setWindowSize(""); setWindowDir("");
    setFloor(""); setRoomType(""); setCalculated(false);
  };

  const isValid = roomLength && roomWidth && ceilingHeight && windowSize && floor && roomType;

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid lg:grid-cols-5 gap-8">
        {/* Form */}
        <div className="lg:col-span-3 bg-slate-50 rounded-2xl p-6 lg:p-8 border border-slate-100">
          <div className="flex items-center gap-2 mb-6">
            <Home className="w-5 h-5 text-brand-600" />
            <h3 className="text-lg font-semibold text-slate-800">Room Details</h3>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Maximize className="w-3.5 h-3.5" /> Room Length (ft)
              </Label>
              <Input type="number" value={roomLength} onChange={e => { setRoomLength(e.target.value); setCalculated(false); }}
                placeholder="e.g. 15" className="h-11 rounded-lg border-slate-200 bg-white focus:border-brand-400 focus:ring-brand-400" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Maximize className="w-3.5 h-3.5" /> Room Width (ft)
              </Label>
              <Input type="number" value={roomWidth} onChange={e => { setRoomWidth(e.target.value); setCalculated(false); }}
                placeholder="e.g. 12" className="h-11 rounded-lg border-slate-200 bg-white focus:border-brand-400 focus:ring-brand-400" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Ceiling Height</Label>
              <Select value={ceilingHeight} onValueChange={v => { setCeilingHeight(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select height" /></SelectTrigger>
                <SelectContent>
                  {ceilingFactors.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">Room Type</Label>
              <Select value={roomType} onValueChange={v => { setRoomType(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {roomTypes.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Users className="w-3.5 h-3.5" /> Occupants: <span className="text-brand-600 text-sm font-bold">{people[0]}</span>
              </Label>
              <Slider value={people} onValueChange={v => { setPeople(v); setCalculated(false); }} min={1} max={10} step={1} className="mt-3" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1</span><span>5</span><span>10</span></div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Sun className="w-3.5 h-3.5" /> Window Size
              </Label>
              <Select value={windowSize} onValueChange={v => { setWindowSize(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select size" /></SelectTrigger>
                <SelectContent>
                  {windowSizes.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Sun className="w-3.5 h-3.5" /> Window Direction <span className="text-slate-400 font-normal">(Optional)</span>
              </Label>
              <Select value={windowDir} onValueChange={v => { setWindowDir(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select direction" /></SelectTrigger>
                <SelectContent>
                  {windowDirections.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                <Layers className="w-3.5 h-3.5" /> Floor Level
              </Label>
              <Select value={floor} onValueChange={v => { setFloor(v); setCalculated(false); }}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200 bg-white"><SelectValue placeholder="Select floor" /></SelectTrigger>
                <SelectContent>
                  {floorOptions.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-3 mt-7">
            <Button onClick={handleCalculate} disabled={!isValid}
              className="flex-1 bg-brand-600 hover:bg-brand-700 text-white h-11 rounded-xl text-sm font-medium shadow-md shadow-brand-200 disabled:opacity-50">
              <Calculator className="w-4 h-4 mr-2" /> Calculate Tonnage
            </Button>
            <Button onClick={handleReset} variant="outline" className="h-11 rounded-xl border-slate-200 text-slate-600 hover:bg-slate-100 px-4">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-2">
          {calculated && result ? (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden h-full animate-fade-in">
              <div className="bg-brand-50 p-6 text-center">
                <Snowflake className="w-8 h-8 text-brand-600 mx-auto mb-3" />
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Recommended AC Size</p>
                <p className="text-4xl font-bold text-brand-600 mb-1">{result.recommendation.tonnage}</p>
                <p className="text-sm text-slate-600">{result.recommendation.system}</p>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Room Area</span>
                  <span className="text-sm font-semibold text-slate-800">{result.area} sq ft</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Cooling Load</span>
                  <span className="text-sm font-semibold text-slate-800">{result.totalBTU.toLocaleString()} BTU</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-xs text-slate-500">Exact Tonnage</span>
                  <span className="text-sm font-semibold text-slate-800">{result.rawTonnage} Ton</span>
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mt-3">
                  <p className="text-xs text-amber-700 flex items-start gap-1.5">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    A professional site inspection is recommended for precise sizing based on insulation, wall material, and sun exposure.
                  </p>
                </div>
                <Button onClick={() => onGetQuote && onGetQuote({
                  calculatorType: "residential",
                  inputs: result.inputs,
                  tonnage: parseFloat(result.rawTonnage),
                  system: result.recommendation.system,
                  displayTonnage: result.recommendation.tonnage,
                })} className="w-full bg-accent-500 hover:bg-accent-600 text-white h-11 rounded-xl mt-4 text-sm font-medium">
                  Get Exact HVAC Design & Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-slate-200 h-full flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center mb-5">
                <Snowflake className="w-7 h-7 text-brand-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">Your Result Will Appear Here</h3>
              <p className="text-sm text-slate-500 mb-6">Fill in room details and click "Calculate Tonnage" for your recommendation.</p>
              <div className="w-full space-y-2 text-left">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Factors considered:</p>
                {["Room area & ceiling height", "Occupancy heat load", "Window size & sun direction", "Floor level thermal gain", "Room usage type", "Indian climate buffer (+8%)"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />
                    <span className="text-xs text-slate-500">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
